import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getFleetSummary,
  getUptimeTrend,
  getPortfolioMetrics,
  getAlerts,
  getRecentStateChanges,
  getServiceSummary,
  getResourceSummary,
  getUserAudit,
  getNodeLifecycleCounts,
  getDeploymentStats,
  getClusterSummary,
  getNetworkSummary,
} from '@/services/api';
import type {
  FleetSummary,
  UptimeTrend,
  UptimeDataPoint,
  PortfolioMetrics,
  Alert,
  Period,
  ServiceSummary,
  ResourceSummary,
  UserAudit,
  SingleMetricStats,
  MetricPeriodStats,
  NodeLifecycleCounts,
  DeploymentStats,
  ClusterSummary,
  NetworkSummary,
} from '@/types/metrics';
import type { StateEvent } from '@/types/node';
import { useDashboardStore } from '@/stores/useDashboardStore';

function useFilters() {
  const selectedProfile = useDashboardStore((state) => state.selectedProfile);
  const selectedVendor = useDashboardStore((state) => state.selectedVendor);
  const selectedVersion = useDashboardStore((state) => state.selectedVersion);
  const selectedPortfolio = useDashboardStore((state) => state.selectedPortfolio);
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  return {
    profile: selectedProfile || undefined,
    vendor: selectedVendor || undefined,
    softwareVersion: selectedVersion || undefined,
    portfolio: selectedPortfolio || undefined,
    refreshKey,
  };
}

export function useFleetSummary() {
  const { profile, vendor, softwareVersion, portfolio, refreshKey } = useFilters();
  const [summary, setSummary] = useState<FleetSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getFleetSummary(profile, vendor, softwareVersion, portfolio);
      setSummary(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch fleet summary'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, vendor, softwareVersion, portfolio, refreshKey]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return { summary, loading, error, refetch: fetchSummary };
}

export function useUptimeTrend(period: Period = '24h', customRange?: { from: Date; to: Date }) {
  const { profile, vendor, softwareVersion, portfolio, refreshKey } = useFilters();
  const [trend, setTrend] = useState<UptimeTrend | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const customFromIso = customRange?.from?.toISOString();
  const customToIso = customRange?.to?.toISOString();

  useEffect(() => {
    async function fetch() {
      try {
        setLoading(true);
        const data = await getUptimeTrend(
          period,
          customRange,
          profile,
          vendor,
          softwareVersion,
          portfolio
        );
        setTrend(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch uptime trend'));
      } finally {
        setLoading(false);
      }
    }
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, customFromIso, customToIso, profile, vendor, softwareVersion, portfolio, refreshKey]);

  return { trend, loading, error };
}

export function usePortfolioMetrics() {
  // Intentionally does NOT use portfolio filter — treemap always shows all portfolios
  const { profile, vendor, softwareVersion, refreshKey } = useFilters();
  const [portfolios, setPortfolios] = useState<PortfolioMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetch() {
      try {
        setLoading(true);
        const data = await getPortfolioMetrics(profile, vendor, softwareVersion);
        setPortfolios(data.portfolios);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch portfolio metrics'));
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, [profile, vendor, softwareVersion, refreshKey]);

  return { portfolios, loading, error };
}

export function useAlerts() {
  const { profile, vendor, softwareVersion, portfolio, refreshKey } = useFilters();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAlerts(profile, vendor, softwareVersion, portfolio);
      setAlerts(data.alerts);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch alerts'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, vendor, softwareVersion, portfolio, refreshKey]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  return { alerts, loading, error, refetch: fetchAlerts };
}

export function useRecentStateChanges(
  limit = 50,
  period = '24h',
  customRange?: { from: Date; to: Date }
) {
  const { profile, vendor, softwareVersion, portfolio, refreshKey } = useFilters();
  const [changes, setChanges] = useState<StateEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const customFromMs = customRange?.from?.getTime();
  const customToMs = customRange?.to?.getTime();

  useEffect(() => {
    async function fetch() {
      try {
        setLoading(true);
        const data = await getRecentStateChanges(
          limit,
          profile,
          vendor,
          softwareVersion,
          portfolio,
          period,
          customRange
        );
        setChanges(data.changes);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch state changes'));
      } finally {
        setLoading(false);
      }
    }
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    limit,
    profile,
    vendor,
    softwareVersion,
    portfolio,
    period,
    customFromMs,
    customToMs,
    refreshKey,
  ]);

  return { changes, loading, error };
}

export function useServiceSummary() {
  const { profile, vendor, softwareVersion, portfolio, refreshKey } = useFilters();
  const [summary, setSummary] = useState<ServiceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetch() {
      try {
        setLoading(true);
        const data = await getServiceSummary(profile, vendor, softwareVersion, portfolio);
        setSummary(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch service summary'));
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, [profile, vendor, softwareVersion, portfolio, refreshKey]);

  return { summary, loading, error };
}

export function useResourceSummary() {
  const { profile, vendor, softwareVersion, portfolio, refreshKey } = useFilters();
  const [summary, setSummary] = useState<ResourceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetch() {
      try {
        setLoading(true);
        const data = await getResourceSummary(profile, vendor, softwareVersion, portfolio);
        setSummary(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch resource summary'));
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, [profile, vendor, softwareVersion, portfolio, refreshKey]);

  return { summary, loading, error };
}

export function useUserAudit() {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [audit, setAudit] = useState<UserAudit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAudit = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getUserAudit();
      setAudit(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch user audit'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  return { audit, loading, error, refetch: fetchAudit };
}

export function useClusterSummary() {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [summary, setSummary] = useState<ClusterSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getClusterSummary();
      setSummary(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch cluster summary'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary, refreshKey]);

  return { summary, loading, error, refetch: fetchSummary };
}

export function useNetworkSummary() {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [summary, setSummary] = useState<NetworkSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getNetworkSummary();
      setSummary(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch network summary'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary, refreshKey]);

  return { summary, loading, error, refetch: fetchSummary };
}

export function useNodeLifecycleCounts(days = 7) {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [counts, setCounts] = useState<NodeLifecycleCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetch() {
      try {
        setLoading(true);
        const data = await getNodeLifecycleCounts(days);
        setCounts(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch lifecycle counts'));
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, [days, refreshKey]);

  return { counts, loading, error };
}

export function useDeploymentStats() {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [stats, setStats] = useState<DeploymentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetch() {
      try {
        setLoading(true);
        const data = await getDeploymentStats();
        setStats(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch deployment stats'));
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, [refreshKey]);

  return { stats, loading, error };
}

function computeStats(values: number[]): { high: number; avg: number; low: number } {
  if (values.length === 0) return { high: 0, avg: 0, low: 0 };
  const high = Math.max(...values);
  const low = Math.min(...values);
  const avg = values.reduce((s, v) => s + v, 0) / values.length;
  return { high, avg, low };
}

function getPreviousRange(
  period: Period,
  customRange?: { from: Date; to: Date }
): { from: Date; to: Date } {
  const now = new Date();
  const periodMs: Record<string, number> = {
    '24h': 24 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
    '30d': 30 * 24 * 60 * 60 * 1000,
    '90d': 90 * 24 * 60 * 60 * 1000,
  };

  if (period === 'custom' && customRange) {
    const duration = customRange.to.getTime() - customRange.from.getTime();
    return {
      from: new Date(customRange.from.getTime() - duration),
      to: new Date(customRange.from.getTime()),
    };
  }

  const duration = periodMs[period] ?? periodMs['24h'];
  return {
    from: new Date(now.getTime() - 2 * duration),
    to: new Date(now.getTime() - duration),
  };
}

export function useMetricPeriodStats(
  period: Period,
  customRange: { from: Date; to: Date } | undefined,
  trend: UptimeTrend | null,
  summary: FleetSummary | null
): MetricPeriodStats {
  const { profile, vendor, softwareVersion, portfolio } = useFilters();
  const [prevTrend, setPrevTrend] = useState<UptimeTrend | null>(null);
  const [prevLoading, setPrevLoading] = useState(false);

  // Stable serialization for effect deps
  const customFromIso = customRange?.from?.toISOString();
  const customToIso = customRange?.to?.toISOString();

  // Fetch previous period trend for delta calculation
  useEffect(() => {
    let cancelled = false;
    async function fetchPrev() {
      setPrevLoading(true);
      try {
        const prevRange = getPreviousRange(period, customRange);
        const data = await getUptimeTrend(
          'custom',
          prevRange,
          profile,
          vendor,
          softwareVersion,
          portfolio
        );
        if (!cancelled) setPrevTrend(data);
      } catch {
        if (!cancelled) setPrevTrend(null);
      } finally {
        if (!cancelled) setPrevLoading(false);
      }
    }
    fetchPrev();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, customFromIso, customToIso, profile, vendor, softwareVersion, portfolio]);

  return useMemo(() => {
    const empty: SingleMetricStats = { high: 0, avg: 0, low: 0, delta: null };
    // Buckets with null uptime are true data gaps (no observations) — exclude
    // them from stats rather than treating them as zeros
    const withData = (points: UptimeDataPoint[]) =>
      points
        .filter((d) => d.uptimePercent !== null)
        .map((d) => ({ ...d, uptimePercent: d.uptimePercent ?? 0 }));
    const dp = trend ? withData(trend.dataPoints) : [];
    if (!trend || dp.length === 0 || !summary) {
      return {
        healthScore: empty,
        uptime: empty,
        totalNodes: empty,
        online: empty,
        offline: empty,
        loading: true,
      };
    }

    // Current version/NTP percentages (stable across period)
    const versionPct =
      summary.totalNodes > 0
        ? (summary.versionCompliance.compliant / summary.totalNodes) * 100
        : 100;
    const ntpPct = summary.ntpSyncPercent ?? 100;

    // Derive per-datapoint values, including the current live value so
    // high/low always encompass what the user sees on the metric cards
    const uptimeVals = [...dp.map((d) => d.uptimePercent), summary.uptimePercent];
    const totalVals = [...dp.map((d) => d.totalCount), summary.totalNodes];
    const onlineVals = [...dp.map((d) => d.aliveCount), summary.aliveNodes];
    const offlineVals = [
      ...dp.map((d) => d.totalCount - d.aliveCount),
      summary.unreachableNodes,
    ];
    const healthVals = [
      ...dp.map((d) => 0.8 * d.uptimePercent + 0.15 * versionPct + 0.05 * ntpPct),
      summary.fleetHealthScore,
    ];

    const uptimeStats = computeStats(uptimeVals);
    const totalStats = computeStats(totalVals);
    const onlineStats = computeStats(onlineVals);
    const offlineStats = computeStats(offlineVals);
    const healthStats = computeStats(healthVals);

    // Delta from previous period
    let deltaUptime: number | null = null;
    let deltaTotalNodes: number | null = null;
    let deltaOnline: number | null = null;
    let deltaOffline: number | null = null;
    let deltaHealth: number | null = null;

    const pdpFiltered = prevTrend ? withData(prevTrend.dataPoints) : [];
    if (pdpFiltered.length > 0 && !prevLoading) {
      const pdp = pdpFiltered;
      const prevUptimeAvg =
        pdp.reduce((s: number, d) => s + d.uptimePercent, 0) / pdp.length;
      const prevTotalAvg =
        pdp.reduce((s: number, d) => s + d.totalCount, 0) / pdp.length;
      const prevOnlineAvg =
        pdp.reduce((s: number, d) => s + d.aliveCount, 0) / pdp.length;
      const prevOfflineAvg =
        pdp.reduce((s: number, d) => s + (d.totalCount - d.aliveCount), 0) /
        pdp.length;
      const prevHealthAvg =
        pdp.reduce(
          (s: number, d) =>
            s + (0.8 * d.uptimePercent + 0.15 * versionPct + 0.05 * ntpPct),
          0
        ) / pdp.length;

      deltaUptime = uptimeStats.avg - prevUptimeAvg;
      deltaTotalNodes = totalStats.avg - prevTotalAvg;
      deltaOnline = onlineStats.avg - prevOnlineAvg;
      deltaOffline = offlineStats.avg - prevOfflineAvg;
      deltaHealth = healthStats.avg - prevHealthAvg;
    }

    return {
      healthScore: { ...healthStats, delta: deltaHealth },
      uptime: { ...uptimeStats, delta: deltaUptime },
      totalNodes: { ...totalStats, delta: deltaTotalNodes },
      online: { ...onlineStats, delta: deltaOnline },
      offline: { ...offlineStats, delta: deltaOffline },
      loading: prevLoading,
    };
  }, [trend, summary, prevTrend, prevLoading]);
}

/** Hook returning `{ name, count }[]` for top services aggregated by name. */
export function useServicesByName() {
  const refreshKey = useDashboardStore((s) => s.refreshKey);
  const [data, setData] = useState<{ name: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    import('@/services/api')
      .then(({ getServicesByName }) => getServicesByName())
      .then((result) => {
        if (!cancelled) setData(result.servicesByName);
      })
      .catch(() => { /* silent for MVP */ })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);
  return { data, loading };
}

/** Hook returning the Basic/Pro/Enterprise breakdown for the Product Tier donut. */
export function useProductTierBreakdown() {
  const refreshKey = useDashboardStore((s) => s.refreshKey);
  const [data, setData] = useState<{ Basic: number; Pro: number; Enterprise: number } | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    import('@/services/api')
      .then(({ getProductTierBreakdown }) => getProductTierBreakdown())
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch(() => { /* silent for MVP */ })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);
  return { data, loading };
}
